import axios from 'axios';

let currentToken = null;
let elevatedToken = null;
let onUnauthorized = null;

export const setAuthToken = (token) => {
  currentToken = token || null;
};

export const setElevatedToken = (token) => {
  elevatedToken = token || null;
};

export const getAuthToken = () => currentToken;
export const getElevatedToken = () => elevatedToken;

export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = typeof fn === 'function' ? fn : null;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5200/api',
  timeout: 60000,
});

const managementToken = () => elevatedToken || currentToken;

const isManagementRequest = (config) => {
  const headers = config?.headers || {};
  return headers['X-Management-Auth'] === '1' || headers['x-management-auth'] === '1';
};

export const attachAuthHeader = (config) => {
  const next = { ...config, headers: { ...(config.headers || {}) } };
  const token = isManagementRequest(config) ? managementToken() : currentToken;
  if (token) {
    next.headers.Authorization = `Bearer ${token}`;
  }
  return next;
};

api.interceptors.request.use(attachAuthHeader);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || '');
    if (status === 401 && !url.includes('/auth/login')) {
      const mgmt = isManagementRequest(error.config || {});
      if (onUnauthorized) onUnauthorized(mgmt ? 'elevated' : 'session');
    }
    return Promise.reject(error);
  }
);

const withManagementAuth = () => ({ headers: { 'X-Management-Auth': '1' } });

const readData = (fn, fallback = null) =>
  fn()
    .then((res) => res.data)
    .catch((err) => {
      if (fallback !== null) return fallback;
      throw err;
    });

const writeData = (fn) => fn().then((res) => res.data);

export const login = (email, password, portal = 'app') =>
  writeData(() => api.post('/auth/login', { email, password, portal }));

export const getMe = (token) => {
  const opts = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
  return api.get('/auth/me', opts).then((res) => res.data);
};

export const getVehicles = () => readData(() => api.get('/vehicles'), []);
export const createVehicle = (payload) => writeData(() => api.post('/vehicles', payload, withManagementAuth()));
export const updateVehicle = (id, payload) => writeData(() => api.patch(`/vehicles/${id}`, payload, withManagementAuth()));
export const deleteVehicle = (id) => writeData(() => api.delete(`/vehicles/${id}`, withManagementAuth()));

export const getDrivers = () => readData(() => api.get('/drivers'), []);
export const createDriver = (payload) => writeData(() => api.post('/drivers', payload, withManagementAuth()));
export const updateDriver = (id, payload) => writeData(() => api.patch(`/drivers/${id}`, payload, withManagementAuth()));
export const deleteDriver = (id) => writeData(() => api.delete(`/drivers/${id}`, withManagementAuth()));

export const getLocations = () => readData(() => api.get('/locations'), []);
export const createLocation = (payload) => writeData(() => api.post('/locations', payload));
export const updateLocation = (id, payload) => writeData(() => api.patch(`/locations/${id}`, payload, withManagementAuth()));
export const deleteLocation = (id) => writeData(() => api.delete(`/locations/${id}`, withManagementAuth()));

export const getTrips = (params) => readData(() => api.get('/trips', { params }), []);
export const getPoTrips = (params) => readData(() => api.get('/trips/po-trips', { params }), []);
export const createTrip = (payload) => writeData(() => api.post('/trips', payload));
export const completeTrip = (tripId, payload) => writeData(() => api.patch(`/trips/${tripId}/complete`, payload));
export const getTripMeterPhoto = (tripId) =>
  api.get(`/trips/${tripId}/meter-photo`, { responseType: 'blob' }).then((res) => res.data);
export const reviewTripMeter = (tripId) =>
  writeData(() => api.patch(`/trips/${tripId}/meter-review`, {}, withManagementAuth()));
export const addTripStops = (tripId, payload) => writeData(() => api.post(`/trips/${tripId}/add-stops`, payload));
export const previewDistance = (stops) => writeData(() => api.post('/maps/distance', { addresses: stops }));

export const getAlerts = () => readData(() => api.get('/alerts'), []);

export const getDashboardSummary = () => readData(() => api.get('/dashboard/summary'), {});

export const getAssignments = (date) =>
  readData(() => api.get('/assignments', { params: { date } }), { assignments: [], activeTrips: [] });

export const saveAssignments = (date, assignments) =>
  writeData(() => api.post('/assignments', { date, assignments }, withManagementAuth()));

export const deleteAssignment = (id) =>
  writeData(() => api.delete(`/assignments/${id}`, withManagementAuth()));

export const getNotificationSettings = () =>
  readData(() => api.get('/notification-settings'), {
    defaultPetrolPricePerLiter: null,
    defaultDieselPricePerLiter: null,
    oilChangeIntervalMileage: 5000,
    reAlertIntervalMileage: 1000,
    defaultWashIntervalDays: 10,
    lowFuelThresholdLiters: 10,
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpFromEmail: '',
    smtpConfigured: false,
    whatsappMode: 'link',
    whatsappAutoSendEnabled: false,
    metaPhoneNumberId: '',
    metaTemplateName: '',
    metaAccessTokenMasked: '',
    metaConfigured: false,
  });

export const updateNotificationSettings = (patch) =>
  writeData(() => api.patch('/notification-settings', patch, withManagementAuth()));

export const testSmtpSettings = () =>
  writeData(() => api.post('/notification-settings/smtp-test', {}, withManagementAuth()));

export const testWhatsappSettings = (payload) =>
  writeData(() => api.post('/notification-settings/whatsapp-test', payload || {}, withManagementAuth()));

export const sendOilChangeAlertEmail = () =>
  writeData(() => api.post('/alerts/oil-change/send', {}, withManagementAuth()));

export const getWashVehicles = (date) =>
  readData(
    () => api.get('/wash', { params: date ? { date } : {} }),
    { vehicles: [], date: date || null }
  );

export const getWashDue = () => readData(() => api.get('/wash/due'), { count: 0, vehicles: [] });

export const getWashDueDashboard = () =>
  readData(() => api.get('/dashboard/wash-due'), { count: 0, vehicles: [] });

export const markVehicleWashed = (vehicleId, date, notes) =>
  writeData(() => api.post(`/wash/${vehicleId}/complete`, {
    ...(date ? { date } : {}),
    ...(notes ? { notes } : {}),
  }));

export const getWashAssignments = (date) =>
  readData(
    () => api.get('/wash-assignments', { params: { date } }),
    { date, assignments: [], washers: [] }
  );

export const getAuthorizedWashers = () =>
  readData(() => api.get('/wash-assignments/washers'), { washers: [] });

export const saveWashAssignments = (date, assignments) =>
  writeData(() => api.post('/wash-assignments', { date, assignments }, withManagementAuth()));

export const getFuelFills = (params) => readData(() => api.get('/fuel-fills', { params }), []);
export const createFuelFill = (payload) => writeData(() => api.post('/fuel-fills', payload));

export const getFuelStatus = () => readData(() => api.get('/fuel'), { thresholdLiters: 10, vehicles: [] });
export const getFuelLowDashboard = () =>
  readData(() => api.get('/dashboard/fuel-low'), { count: 0, thresholdLiters: 10, vehicles: [] });

export const getOilChangeVehicles = () =>
  readData(() => api.get('/oil-change'), { count: 0, dueCount: 0, vehicles: [] });

export const getOilChangeDue = () =>
  readData(() => api.get('/dashboard/oil-change-due'), { count: 0, vehicles: [] });

export const recordOilChange = (vehicleId, payload) =>
  writeData(() => api.post(`/oil-change/${vehicleId}/complete`, payload));

export const getDayEndChecklist = (date) =>
  readData(() => api.get('/day-end', { params: { date } }), { date, items: [], summary: { open: 0, done: 0 } });

export const checkoffDayEndItem = ({ date, category, refId, notes }) =>
  writeData(() => api.post('/day-end/checkoff', { date, category, refId, notes }));

export default api;
